// Meeting scheduler client. The website backend forwards these calls (signed) to the Contextral dashboard,
// which owns leads, availability and bookings.
const API_BASE = import.meta.env.VITE_API_BASE || "";
const SCHEDULER = `${API_BASE}/api/scheduler`;

export type MeetingType = "assessment" | "sow_review";
/** What a booked meeting can be: website bookings, plus meetings the Contextral team schedules. */
export type BookedMeetingType = MeetingType | "implementation" | "handoff" | "other";

const MEETING_TITLES: Record<BookedMeetingType, string> = {
  assessment: "Automation Assessment",
  sow_review: "SOW Review",
  implementation: "Implementation Session",
  handoff: "Deliverable Handoff",
  other: "Meeting",
};

/** A meeting's name: the title the team gave it, else its type's. */
export function meetingTitle(meeting: { meeting_type: BookedMeetingType; title?: string | null }): string {
  return meeting.title || MEETING_TITLES[meeting.meeting_type] || "Meeting";
}

export interface IntakeDetails {
  first_name: string;
  last_name: string;
  job_title: string;
  company: string;
  email: string;
  timezone: string;
  utm_source?: string;
  utm_id?: string;
  /** The website referral code the visitor arrived with; the website backend sends its owner to the dashboard. */
  referred_by?: string;
}

export interface Intake {
  intake_id: string;
  expires_at: string;
  lead: { name: string };
  meeting_minutes: number;
  timezone: string;
}

export interface Availability {
  timezone: string;
  meeting_type: MeetingType;
  meeting_minutes: number;
  lead: { name: string };
  /** Bookable start times, ISO 8601 in UTC. */
  slots: string[];
  /**
   * A SOW review link whose review is already booked: that booking, with its manage link. Or another upcoming
   * meeting for this client (one at a time), shown without a manage link.
   */
  existing_booking?: (Booking & { same_person: true }) | ExistingBooking;
}

/**
 * A meeting this visitor (or someone at their company) already has. The public form never returns the manage
 * link: the person who booked is pointed to their calendar invite; a colleague only learns a meeting exists.
 */
export type ExistingBooking =
  | { same_person: true; meeting_type: BookedMeetingType; title?: string | null; company: string;
      starts_at: string; ends_at: string; timezone: string; lead: { name: string }; manage_token?: string }
  | { same_person: false; meeting_type: BookedMeetingType; title?: undefined; company: string };

export type IntakeResult = Intake | { existing_booking: ExistingBooking };

export interface Guest {
  email: string;
  name: string;
}

export interface Booking {
  id: string;
  meeting_type: BookedMeetingType;
  title?: string | null;
  starts_at: string;
  ends_at: string;
  timezone: string;
  lead: { name: string };
  company: string;
  meet_url: string | null;
  cancelled: boolean;
  /** The lead cancelled and emailed this link so the client can pick a new time (can_change stays true). */
  cancelled_by_lead?: boolean;
  can_change: boolean;
  /** Extra people the client invited; they are on the calendar invite. */
  guests?: Guest[];
  manage_token: string;
  manage_url: string;
}

export class SchedulerError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly status: number,
    readonly slots: string[] | null = null,
    readonly fields: string[] = [],
    readonly existing: ExistingBooking | null = null,
  ) {
    super(message);
  }
}

const FALLBACK_MESSAGE = "Something went wrong. Please try again or email hello@contextral.com.";

async function request<T>(path: string, init: RequestInit = {}, idempotencyKey?: string): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${SCHEDULER}${path}`, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}),
      },
    });
  } catch {
    throw new SchedulerError("We couldn't reach our scheduler. Check your connection and try again.", "NETWORK", 0);
  }
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = body?.error ?? {};
    throw new SchedulerError(
      error.message || FALLBACK_MESSAGE,
      error.code || "BOOKING_ERROR",
      response.status,
      Array.isArray(error.details?.slots) ? error.details.slots : null,
      Array.isArray(error.details?.fields) ? error.details.fields : [],
      error.details?.existing_booking ?? null,
    );
  }
  return body as T;
}

/** One key per user action, reused if that action is retried, so the dashboard books it only once. */
export function newIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID().replace(/-/g, "");
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
}

/** The visitor's IANA timezone, with a sensible default when the browser does not report one. */
export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";
  } catch {
    return "America/New_York";
  }
}

export const schedulerApi = {
  createIntake: (details: IntakeDetails) =>
    request<IntakeResult>("/intake/", { method: "POST", body: JSON.stringify(details) }),

  availability: (params: { intake?: string; token?: string; timezone?: string }) => {
    const query = new URLSearchParams(
      Object.entries(params).filter((entry): entry is [string, string] => Boolean(entry[1])),
    );
    return request<Availability>(`/availability/?${query.toString()}`);
  },

  book: (body: { intake_id?: string; token?: string; timezone?: string; start: string; guests?: Guest[] },
         key: string) =>
    request<{ booking: Booking }>("/bookings/", { method: "POST", body: JSON.stringify(body) }, key),

  manage: (token: string) =>
    request<{ booking: Booking }>(`/bookings/manage/?${new URLSearchParams({ token }).toString()}`),

  reschedule: (token: string, start: string, key: string) =>
    request<{ booking: Booking }>("/bookings/reschedule/", {
      method: "POST",
      body: JSON.stringify({ token, start }),
    }, key),

  /** Emails the meeting's reschedule link to whoever booked it; the link itself never reaches the browser. */
  emailManageLink: (details: { email: string; company: string }) =>
    request<{ sent: boolean; same_person: boolean }>("/bookings/email-link/", {
      method: "POST",
      body: JSON.stringify(details),
    }),

  cancel: (token: string, key: string) =>
    request<{ booking: Booking }>("/bookings/cancel/", { method: "POST", body: JSON.stringify({ token }) }, key),
};

/** Group start times by the client's local calendar day, earliest first. */
export function groupSlotsByDay(slots: string[], timeZone: string): { day: string; label: string; slots: string[] }[] {
  const dayKey = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" });
  const dayLabel = new Intl.DateTimeFormat("en-US", { timeZone, weekday: "short", month: "short", day: "numeric" });
  const groups = new Map<string, { day: string; label: string; slots: string[] }>();
  for (const slot of [...slots].sort()) {
    const date = new Date(slot);
    const day = dayKey.format(date);
    if (!groups.has(day)) groups.set(day, { day, label: dayLabel.format(date), slots: [] });
    groups.get(day)!.slots.push(slot);
  }
  return [...groups.values()];
}

export function formatTime(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric", minute: "2-digit" }).format(new Date(iso));
}

export function formatMeeting(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone, weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short",
  }).format(new Date(iso));
}

/** A readable zone name such as "Eastern Daylight Time (America/New_York)". */
export function describeTimezone(timeZone: string, at: Date = new Date()): string {
  const part = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "long" })
    .formatToParts(at)
    .find((p) => p.type === "timeZoneName");
  return part ? `${part.value} (${timeZone})` : timeZone;
}
